<template>
  <div v-if="displayWorkflowFallbackMessage" data-qa="preview-content-push">
    <div class="fallback-msg-ct">
      {{ $t('CampaignForm_Preview_ContentMessageDetails_NoMessages') }}
    </div>
  </div>
  <div v-else data-qa="preview-content-push">
    <push-test-send-dialog
      v-if="canShowSendPreview"
      :dialog-visible.sync="showSendPreview"
      :preview="previewSendData"
    />
    <div class="flexbox">
      <div class="controls-container">
        <el-radio-group
          v-if="showPlatformToggle"
          v-model="platform"
          size="small"
          data-qa="preview-push-platform-toggle"
          @change="getPreview()"
        >
          <el-radio-button label="ios" data-qa="preview-push-platform-ios">
            iOS
          </el-radio-button>
          <el-radio-button
            label="android"
            data-qa="preview-push-platform-android"
          >
            Android
          </el-radio-button>
        </el-radio-group>
      </div>
    </div>
    <div v-loading="loading" class="preview-body" data-qa="preview-push-body">
      <div
        class="push-preview-container"
        :class="{ 'push-preview-container--split': hasSidePanel }"
        data-qa="preview-push-device"
      >
        <push-preview-section
          v-if="renderedPlatform"
          :platform="renderedPlatform"
          :app-icon="appIcon"
          :app-name="appName"
          :title="content.title"
          :text="content.alert"
          :icon="content.picture"
          :actions="categoryActions"
          :new-frame="true"
        />
      </div>
      <div
        v-if="hasSidePanel"
        class="preview-details"
        data-qa="preview-push-side-panel"
      >
        <preview-default-action
          :url="defaultActionUrl"
          :deeplink="defaultActionDeeplink"
          data-qa-prefix="preview-push"
        />
        <preview-action-buttons
          :buttons="categoryButtons"
          data-qa-prefix="preview-push"
        />
        <preview-payload :items="payloadMap" data-qa-prefix="preview-push" />
      </div>
    </div>
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import { PreviewChannelEnum } from '@/modules/campaign-manage/enum/PreviewChannelEnum'
import { EventBus } from '@/core/EventBus'
import { Message } from 'element-ui'
import _ from 'lodash'
import PushPreviewSection from '@/common/components/push/PushPreviewSection.vue'
import PushTestSendDialog from '@/modules/campaign-manage/components/preview/PushTestSendDialog.vue'
import PreviewDefaultAction from '@/modules/campaign-manage/components/preview/PreviewDefaultAction.vue'
import PreviewActionButtons from '@/modules/campaign-manage/components/preview/PreviewActionButtons.vue'
import PreviewPayload from '@/modules/campaign-manage/components/preview/PreviewPayload.vue'
import PreviewPayloadMapMixin from '@/modules/campaign-manage/mixins/PreviewPayloadMapMixin'
import {
  processCustomAttributes,
  normaliseUnmetConditions
} from '@/modules/campaign-manage/helpers/PreviewAttributeHelper'

export default {
  name: 'PreviewContentPush',
  mixins: [PreviewPayloadMapMixin],
  components: {
    PushPreviewSection,
    PushTestSendDialog,
    PreviewDefaultAction,
    PreviewActionButtons,
    PreviewPayload
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    variantKey: {
      type: String,
      required: true
    },
    workflowAction: {
      type: Object,
      required: false,
      default: () => ({})
    },
    language: {
      type: String,
      required: false
    },
    profileId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Full profile object corresponding to `profileId` — used by the test-send
     * dialog to render a human label for the selected profile in its description.
     */
    selectedProfile: {
      type: Object,
      required: false,
      default: null
    },
    customAttributes: {
      type: Object,
      required: false,
      default: null
    },
    pushPlatform: {
      type: String,
      required: false,
      default: null
    }
  },
  mounted() {
    if (this.extendedContentPreview) {
      EventBus.$on('showSendPreview', this.onShowSendPreview)
    }
    if (this.displayWorkflowFallbackMessage) {
      this.loading = false
      EventBus.$emit('set-profile-attributes', {})
      EventBus.$emit('set-unmet-attribute-conditions', {})
      return
    }
    if (!this.pushPlatform) {
      this.$emit('push-platform-change', this.defaultPlatform)
    }
    this.getPreview()
  },
  beforeDestroy() {
    EventBus.$off('showSendPreview', this.onShowSendPreview)
  },
  data() {
    return {
      loading: true,
      content: {},
      renderedPlatform: null,
      showSendPreview: false
    }
  },
  computed: {
    platform: {
      get() {
        return this.pushPlatform || this.defaultPlatform
      },
      set(value) {
        this.$emit('push-platform-change', value)
      }
    },
    defaultPlatform() {
      if (this.workflowAction && this.workflowAction.platform) {
        return this.workflowAction.platform
      }
      if (this.model.ios_push === 1) return 'ios'
      if (this.model.android_push === 1) return 'android'
      return 'ios'
    },
    appName() {
      return this.$store.state.app.project.name || ''
    },
    appIcon() {
      return this.$store.state.app.project.icon || ''
    },
    messageType() {
      return this.platform === 'ios'
        ? channelTypes.ios_push
        : channelTypes.android_push
    },
    showPlatformToggle() {
      if (this.model.workflow) {
        return false
      }
      return this.model.ios_push === 1 && this.model.android_push === 1
    },
    defaultActionUrl() {
      return this.content.default_action_url || ''
    },
    defaultActionDeeplink() {
      return this.content.default_action_deeplink || ''
    },
    categoryActionDetails() {
      const actions = this.content && this.content.category_actions
      if (!Array.isArray(actions)) {
        return []
      }
      return actions.filter(
        a => typeof a.title === 'string' && a.title.trim() !== ''
      )
    },
    categoryActions() {
      // PushPreviewSection expects a flat array of title strings.
      return this.categoryActionDetails.map(a => a.title)
    },
    categoryButtons() {
      return this.categoryActionDetails.map(a => ({
        label: a.title,
        url: a.url,
        deeplink: a.deeplink
      }))
    },
    hasSidePanel() {
      return (
        !!this.defaultActionUrl ||
        !!this.defaultActionDeeplink ||
        this.categoryActionDetails.length > 0 ||
        this.payloadMap.length > 0
      )
    },
    displayWorkflowFallbackMessage() {
      return Boolean(this.model.workflow) && _.isEmpty(this.workflowAction)
    },
    extendedContentPreview() {
      return !!this.$hasFeature('extendedContentPreview')
    },
    canShowSendPreview() {
      return this.showSendPreview && this.extendedContentPreview
    },
    previewSendData() {
      return {
        campaign: this.model,
        message_type: this.messageType,
        variant_key: this.variantKey,
        workflow_action: this.workflowAction,
        language: this.language,
        profile_id: this.profileId,
        selected_profile: this.selectedProfile,
        customAttributes: this.processCustomAttributes(this.customAttributes)
      }
    }
  },
  watch: {
    customAttributes: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.getPreview()
        }
      },
      deep: true
    }
  },
  methods: {
    processCustomAttributes,
    onShowSendPreview(payload) {
      if (!payload || payload.channel === PreviewChannelEnum.PUSH) {
        this.showSendPreview = true
      }
    },
    getPreview() {
      this.loading = true
      this.content = {}
      this.renderedPlatform = null
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaign-messages/preview`,
          data: {
            campaign: this.model,
            message_type: this.messageType,
            variant_key: this.variantKey,
            workflow_action: this.workflowAction,
            language: this.language,
            profile_id: this.profileId,
            customAttributes: this.processCustomAttributes(
              this.customAttributes
            )
          }
        })
      )
        .then(({ data }) => {
          if (this._isDestroyed) return
          this.renderedPlatform = this.platform
          this.content = data['message']
          EventBus.$emit('set-profile-attributes', data.attributes)
          if (!this.customAttributes && this.profileId) {
            EventBus.$emit('set-profile-id', this.profileId)
          }
          EventBus.$emit(
            'set-unmet-attribute-conditions',
            normaliseUnmetConditions(data['unmetConditions'])
          )
        })
        .catch(error => {
          Message.closeAll()
          this.$message({
            message: error.message,
            type: 'error',
            duration: 5000
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.controls-container {
  padding: 20px 0;
}

.preview-body {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.push-preview-container {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  min-height: 400px;
  width: 100%;
}

.push-preview-container--split {
  width: 50%;
}

.preview-details {
  width: 50%;
  padding-top: 20px;
  font-size: 12px;
  color: #606266;
  min-width: 0;
}

.side-panel-section {
  padding-top: 4px;
}

.fallback-msg-ct {
  margin-top: 30%;
  margin-right: 50%;
  margin-left: 50%;
  width: 300px;
  font-size: 16px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PreviewContentPush.vue